import { useCallback, useEffect, useMemo } from "react";

import moment from "moment";
import { useHistory, useLocation } from "react-router-dom";

import { CalendarUrlParams } from "../types/fast";
import { isCalendarView } from "../utils/typePredicate";

import type { CalendarView } from "../types/fast";

const useCalendarUrlParams = (): {
  currentDate: string;
  currentView: CalendarView;
  updateCalendarUrlParams: (params: {
    date?: Date;
    view?: CalendarView;
  }) => void;
} => {
  const history = useHistory();
  const { search, pathname } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const defaultDate = moment().format("YYYY-MM-DD");
  const defaultView = "month" as const;

  const currentDate = searchParams.get(CalendarUrlParams.Date) ?? defaultDate;
  const currentView = (searchParams.get(CalendarUrlParams.View) ??
    defaultView) as CalendarView;

  const replaceHistory = useCallback(
    ({
      date,
      view = defaultView,
    }: {
      date?: Date;
      view?: CalendarView;
    } = {}): void => {
      const dateString = moment(date ?? defaultDate).format("YYYY-MM-DD");
      history.replace(
        `?${CalendarUrlParams.Date}=${dateString}&${CalendarUrlParams.View}=${view}`,
      );
    },
    [defaultDate, defaultView, history],
  );

  const updateCalendarUrlParams = useCallback(
    ({
      date = new Date(currentDate),
      view = currentView,
    }: {
      date?: Date;
      view?: CalendarView;
    }): void => {
      replaceHistory({ date, view });
    },
    [currentDate, currentView, replaceHistory],
  );

  useEffect(() => {
    const calendarPathRegex =
      /^\/fast-channels\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/calendar/;
    const isCalendarPath = calendarPathRegex.test(pathname);

    if (
      isCalendarPath &&
      (!searchParams.has(CalendarUrlParams.Date) ||
        !searchParams.has(CalendarUrlParams.View) ||
        !isCalendarView(currentView))
    ) {
      replaceHistory();
    }
  }, [currentView, pathname, searchParams, replaceHistory]);

  return {
    currentDate,
    currentView,
    updateCalendarUrlParams,
  };
};

export default useCalendarUrlParams;
